import type { ArchiveFormSectionRule } from '../../../component/archive-form';

import type {

    CardFormChildrenConfig,

    CardFormConfig,

} from '../../../component/card/card-from';

import {

    COUNTRY_REG_CITY_REGULAR,

    COUNTRY_REG_NAME_REGULAR,

    COUNTRY_REG_POSTCODE_REGULAR,

    COUNTRY_VAT_REGULAR,

} from '../../../constants/regular.constant';

import {

    SPAIN_CITY_AREA,

} from '../../../constants/spain-city-area';



/** 餐厅建档分区配置上下文。 */

export interface RestaurantArchiveContext {

    /** 页面模式。 */

    componentType?: 'ADD' | 'EDIT' | 'PREVIEW' | 'BASE_INFO';

    /** 所在国家电话区号。 */

    countryCode: string;

    /** 当前账号是否为经销商。 */

    isReseller: boolean;

    /** 餐厅来源版本。 */

    restaurantVersion: string;

}


/** 餐厅保存校验配置上下文。 */

export interface RestaurantArchiveValidationContext {

    /** 页面模式。 */

    componentType?: 'ADD' | 'EDIT' | 'PREVIEW' | 'BASE_INFO';

    /** 所在国家电话区号。 */

    countryCode: string;

    /** 是否使用双数据库端口。 */

    isMultiDatabasePortMode: boolean;

}



/** 核心分区提示文案 i18n key。 */

export const RESTAURANT_ARCHIVE_SECTION_HINTS: Partial<Record<string, string>> = {

    identity: 'archive_form_restaurant_identity_hint',

    'tax-and-address': 'archive_form_restaurant_tax_hint',

    business: 'archive_form_restaurant_business_hint',

};



/** 创建标准输入字段配置，确保每次调用都返回新对象。 */

const createInputField = (

    key: string,

    label: string,

    options: Partial<CardFormChildrenConfig> = {},

): CardFormChildrenConfig => ({

    key,

    label,

    comType: 'INPUT',

    inputType: 'INPUT',

    xs: 12,

    md: 6,

    ...options,

});



/** 创建餐厅身份分区字段。 */

const createIdentityFields = (

    context: RestaurantArchiveContext,

): CardFormChildrenConfig[] => {

    /** 新增态不展示由系统生成或编辑态维护的字段。 */

    const isAdd = context.componentType === 'ADD';

    return [

        {

            key: 'avatar',

            comType: 'AVATAR',

            avatarSize: 64,
            className: 'restaurant-info-avatar-field',

            avatarCompact: true,

            xs: 12,

            md: 2,

        },

        createInputField('nickname', 'restaurant_info_page_text_restaurant_name', {

            required: true,

            regularType: 'inputLimit120',

            md: 6,

        }),

        createInputField('country_code', 'restaurant_Country_and_Region', {

            required: true,

            inputType: 'FLAG_INPUT',

            disabledInput: !isAdd,

            md: 6,

        }),

        createInputField('belonged_group', 'general_text_belonged_group', {

            required: true,

            inputType: 'SELECT',

            md: 6,

        }),

        createInputField('string_id', 'restaurant_page_input_text_restaurant_identification_code', {

            disabledInput: true,

            inputProps: isAdd

                ? { placeholder: 'archive_form_string_id_placeholder' }

                : undefined,

            md: 6,

        }),

        createInputField('ex_string_id', 'restaurant_page_ex_string_id', {

            hidden: isAdd,

            md: 6,

        }),

        createInputField('setting_restaurant_operating_status', 'restaurant_info_page_status', {

            hidden: isAdd,

            inputType: 'SELECT',

            md: 6,

            items: [

                { value: '1', label: 'restaurant_info_page_restaurant_open' },

                { value: '2', label: 'restaurant_info_page_restaurant_close' },

                { value: '3', label: 'restaurant_page_text_restaurant_change_ditta' },

                { value: '4', label: 'restaurant_info_page_restaurant_transfer' },

                { value: '5', label: 'restaurant_info_page_restaurant_pause' },

                { value: '6', label: 'restaurant_info_page_restaurant_changed_system' },

                { value: '7', label: 'restaurant_info_page_restaurant_sosreso' },

                { value: '8', label: 'restaurant_info_page_restaurant_annullato' },

            ],

        }),

        {

            key: 'description',

            label: 'archive_form_restaurant_description_fold',

            comType: 'CUSTOMCHILDREN',

            xs: 12,

            md: 12,

        },

    ];

};



/** 创建税务与双地址分区字段。 */

const createTaxAndAddressFields = (

    countryCode: string,

): CardFormChildrenConfig[] => {

    /** 当前国家是否为意大利。 */

    const isItaly = countryCode === '+39';

    /** 当前国家是否为西班牙。 */

    const isSpain = countryCode === '+34';

    return [

        createInputField('reg_name', 'archive_form_restaurant_company_name', {

            required: true,

            regularType: COUNTRY_REG_NAME_REGULAR[countryCode],

            md: 12,

        }),

        createInputField('reg_fiscal_code', 'restaurant_info_page_text_fiscal_code', {

            required: isItaly,

            regularType: isItaly ? 'vatNumber' : undefined,

            md: 4,

        }),

        createInputField('reg_vat_number', 'restaurant_info_page_text_vat_number', {

            required: isItaly || isSpain,

            regularType: COUNTRY_VAT_REGULAR[countryCode],

            md: 4,

        }),

        createInputField('reg_des_code', 'restaurant_info_page_text_client_recipient_code', {

            regularType: 'recipientCode',

            hidden: !isItaly,

            md: 4,

        }),

        createInputField('reg_pec', 'create_group_component_text_pec_email', {

            required: isItaly,

            regularType: 'email',

            md: 12,

        }),

        {

            key: 'dual_address',

            comType: 'CUSTOMCHILDREN',

            xs: 12,

            md: 12,

        },

    ];

};



/**
 * 创建联系人分区整行副标题。
 * @param key 字段标识
 * @param label 副标题 i18n key
 * @param className 栅格项样式类
 */

const createContactHeading = (

    key: string,

    label: string,

    className: string,

): CardFormChildrenConfig => ({

    key,

    label,

    comType: 'CUSTOMCHILDREN',

    xs: 12,

    md: 12,

    className,

});



/** 创建联系人分区字段。 */

const createContactFields = (): CardFormChildrenConfig[] => [

    createContactHeading(

        'contact_in_store_heading',

        'archive_form_restaurant_contact_in_store',

        'restaurant-archive-subheading-field restaurant-archive-subheading-field--first',

    ),

    createInputField('restaurant_contact_person', 'restaurant_info_page_text_contact_person', {

        md: 4,

    }),

    createInputField('tel_prefix', 'general_text_country_code', {

        inputType: 'COUNTRY_SELECT',

        md: 2,

    }),

    createInputField('tel', 'restaurant_info_page_text_phone', {

        required: true,

        regularType: 'phone',

        md: 6,

    }),

    createInputField('email', 'general_text_email', {

        regularType: 'email',

        md: 6,

    }),

    createInputField('website', 'restaurant_info_page_text_website', {

        md: 6,

    }),

    createContactHeading(

        'contact_accountant_heading',

        'archive_form_restaurant_contact_accountant',

        'restaurant-archive-subheading-field',

    ),

    createInputField('accountant_name', 'restaurant_info_page_text_contact_person', {

        md: 4,

    }),

    createInputField('accountant_phone_prefix', 'general_text_country_code', {

        inputType: 'COUNTRY_SELECT',

        md: 2,

    }),

    createInputField('accountant_phone', 'restaurant_info_page_text_phone', {

        regularType: 'phone',

        md: 6,

    }),

];



/** 创建商务与系统配置分区字段。 */

const createBusinessFields = (): CardFormChildrenConfig[] => [

    createInputField('belonged_reseller', 'reseller_page_text_reseller', {

        inputType: 'SELECT',

        md: 4,

    }),

    createInputField('authorization_type', 'restaurant_info_page_text_way_of_authorization', {

        inputType: 'SELECT',

        md: 4,

    }),

    createInputField('business_labels', 'restaurant_info_page_text_service_instance_tag', {

        inputType: 'SELECT_MUL',

        md: 4,

    }),

    createInputField('discount_info', 'restaurant_info_page_text_discount_information_discount_module', {

        contentType: 'number',

        md: 4,

    }),

    createInputField('discount_annual', 'restaurant_info_page_text_discount_information_discount_annual', {

        contentType: 'number',

        md: 4,

    }),

    createInputField('time_zone', 'restaurant_info_page_text_time_zone', {

        inputType: 'SELECT',

        md: 4,

    }),

    createInputField('restaurant_version', 'restaurant_page_input_text_restaurant_version_source', {

        inputType: 'SELECT',

        md: 4,

    }),

    {

        key: 'customer_mark',

        label: 'archive_form_restaurant_remark_fold',

        comType: 'CUSTOMCHILDREN',

        xs: 12,

        md: 12,

    },

];



/**

 * 返回餐厅四个核心 CardFrom 分区。

 * @param context 页面模式、国家与账号上下文

 * @returns 可直接交给 CardFrom 的新配置数组

 */

export function getRestaurantCoreCardConfig(

    context: RestaurantArchiveContext,

): CardFormConfig[] {

    return [

        {

            key: 'identity',

            title: 'archive_form_restaurant_identity',

            rowSpacing: 3,

            columnSpacing: { md: 3, xs: 3 },

            md: 12,

            children: createIdentityFields(context),

        },

        {

            key: 'tax-and-address',

            title: 'archive_form_restaurant_tax_and_invoice',

            rowSpacing: 3,

            columnSpacing: { md: 3, xs: 3 },

            md: 12,

            children: createTaxAndAddressFields(context.countryCode),

        },

        {

            key: 'contacts',

            title: 'archive_form_restaurant_contacts',

            rowSpacing: 3,

            columnSpacing: { md: 3, xs: 3 },

            md: 12,

            children: createContactFields(),

        },

        {

            key: 'business',

            title: 'archive_form_restaurant_business',

            rowSpacing: 3,

            columnSpacing: { md: 3, xs: 3 },

            md: 12,

            children: createBusinessFields(),

        },

    ];

}



/**

 * 返回餐厅建档核心分区及字段元数据。

 * @param context 页面模式、国家与账号上下文

 * @returns 全新且互不共享引用的分区数组

 */

export function getRestaurantArchiveSections(

    context: RestaurantArchiveContext,

): ArchiveFormSectionRule[] {

    /** 四个核心卡片配置。 */

    const cards = getRestaurantCoreCardConfig(context);

    /** 每个核心分区的信息来源。 */

    const sources = ['SALES', 'DOCUMENT', 'SALES', 'INTERNAL'] as const;

    return cards.map((card, index) => ({

        key: card.key,

        title: String(card.title),

        source: sources[index],

        fields: (card.children ?? []).map((field) => ({

            key: field.key,

            label: field.label ?? field.key,

            required: field.required,

            regularType: field.regularType,

            hidden: field.hidden,

            disabled: field.disabledInput,

        })),

    }));

}



/**

 * 返回保存前校验使用的不可变配置。

 * @param context 国家、页面模式与端口模式

 * @returns checkCardFromRule 可直接消费的新配置数组

 */

export function getRestaurantArchiveValidationConfig(

    context: RestaurantArchiveValidationContext,

): CardFormConfig[] {

    /** 校验阶段只消费字段规则，账号和版本不改变核心必填定义。 */

    const coreCards = getRestaurantCoreCardConfig({

        componentType: context.componentType,

        countryCode: context.countryCode,

        isReseller: false,

        restaurantVersion: '',

    });

    /** 双地址字段沿用既有国家正则；字段为空时 CommonInput 规则不会阻断。 */

    const addressValidationFields: CardFormChildrenConfig[] = [

        createInputField('local', 'address_component_text_address', {

            inputType: 'GOOGLE_PLACE',

        }),

        createInputField('home_umber', 'restaurant_info_page_text_home_number'),

        createInputField('postcode', 'restaurant_info_page_text_postcode'),

        createInputField('city', 'restaurant_info_page_text_city'),

        createInputField('state', 'restaurant_info_page_text_state'),

        createInputField('country', 'restaurant_info_page_text_country'),

        createInputField('reg_local', 'address_component_text_address', {

            regularType: COUNTRY_REG_CITY_REGULAR[context.countryCode],

        }),

        createInputField('reg_additional_address', 'restaurant_info_page_text_additional_address'),

        createInputField('reg_postcode', 'restaurant_info_page_text_postcode', {

            regularType: COUNTRY_REG_POSTCODE_REGULAR[context.countryCode],

        }),

        createInputField('reg_city', 'restaurant_info_page_text_city', {

            regularType: COUNTRY_REG_CITY_REGULAR[context.countryCode],

        }),

        createInputField('reg_state', 'restaurant_info_page_text_state', {
            // 法国：空不拦；有值校法语地址字符集
            regularType: context.countryCode === '+33' ? 'franceFiskalyStateOptional' : 'regName',
        }),

        createInputField('reg_country', 'restaurant_info_page_text_country', {
            regularType: context.countryCode === '+33' ? 'franceCountryFR' : 'regName',
        }),

        createInputField('reg_municipality', 'restaurant_info_page_text_district', {

            regularType: 'regNameSpain120',

            hidden: context.countryCode !== '+34',

        }),

        createInputField('reg_street_number', 'restaurant_info_page_text_number', {
            regularType: context.countryCode === '+33' ? 'franceFiskalyAddress20' : 'regNameSpain20',
            hidden: context.countryCode !== '+34' && context.countryCode !== '+33',
        }),

        createInputField('reg_territory', 'restaurant_info_page_text_region', {

            inputType: 'SELECT',

            items: SPAIN_CITY_AREA,

            hidden: context.countryCode !== '+34',

        }),

    ];

    /** 单端口隧道分区仍沿用旧版主机、端口格式规则。 */

    const tunnelValidationFields: CardFormChildrenConfig[] = [

        createInputField('database_host', 'restaurant_page_input_text_restaurant_host', {

            regularType: 'description',

        }),

        ...(context.isMultiDatabasePortMode

            ? []

            : [createInputField('database_port', 'restaurant_page_input_text_restaurant_port', {

                regularType: 'maxNumberSix',

            })]),

        createInputField('port', 'restaurant_info_page_text_local_port', {

            regularType: 'maxNumberSix',

        }),

    ];

    /** 法人分区只在西班牙餐厅可见，并保留旧版地址格式规则。 */

    const legalPersonValidationFields: CardFormChildrenConfig[] = [

        createInputField('corp_name', 'warrant_page_text_legal_entity_name', {

            regularType: COUNTRY_REG_NAME_REGULAR[context.countryCode],

        }),

        createInputField('corp_vat_number', 'restaurant_info_page_text_vat_number', {

            regularType: COUNTRY_VAT_REGULAR[context.countryCode],

        }),

        createInputField('corp_local', 'address_component_text_address', {

            regularType: COUNTRY_REG_CITY_REGULAR[context.countryCode],

        }),

        createInputField('corp_postcode', 'restaurant_info_page_text_postcode', {

            regularType: COUNTRY_REG_POSTCODE_REGULAR[context.countryCode],

        }),

        createInputField('corp_city', 'restaurant_info_page_text_city', {

            regularType: COUNTRY_REG_CITY_REGULAR[context.countryCode],

        }),

        createInputField('corp_state', 'restaurant_info_page_text_state', {

            regularType: 'regName',

        }),

        createInputField('corp_country', 'restaurant_info_page_text_country', {

            regularType: 'regName',

        }),

        createInputField('corp_municipality', 'restaurant_info_page_text_district', {

            regularType: 'regNameSpain120',

        }),

        createInputField('corp_street_number', 'restaurant_info_page_text_number', {

            regularType: 'regNameSpain20',

        }),

    ];

    /** 核心分区先移除自定义渲染字段，再合并当前可见扩展分区规则。 */

    const normalizedCoreCards = coreCards.map((card) => (

        card.key === 'tax-and-address'

            ? {

                ...card,

                children: [

                    ...(card.children ?? []).filter((field) => field.key !== 'dual_address'),

                    ...addressValidationFields,

                ].filter((field) => !field.hidden),

            }

            : {

                ...card,

                children: (card.children ?? []).filter((field) => (

                    field.comType !== 'CUSTOMCHILDREN' && !field.hidden

                )),

            }

    ));

    return [

        ...normalizedCoreCards,

        {

            key: 'tunnel',

            title: 'restaurant_page_input_text_restaurant_tunnel_info_configuration',

            children: tunnelValidationFields,

        },

        ...(context.countryCode === '+34'

            ? [{

                key: 'legal_person',

                title: 'warrant_page_text_legal_entity_info',

                children: legalPersonValidationFields,

            }]

            : []),

    ];

}

